import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  HelpOutline as HelpOutlineIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import WellsTab from '../analytics/WellsTab';
import ProductionTab from '../analytics/ProductionTab';
import InspectionsTab from '../analytics/InspectionsTab';
import ViolationsTab from '../analytics/ViolationsTab';
import SeverancesTab from '../analytics/SeverancesTab';
import SpillsTab from '../analytics/SpillsTab';
import RelationalTab from '../analytics/RelationalTab';
import MatchesCard from './MatchesCard';
import { generateLeaseKey } from '../../utils/leaseKeyUtils';

const AnalyticsSection = ({
  searchResults,
  searchParams,
  activityDateFilters,
  wellDateFilters,
  resultsFilter,
  activeTab,
  setActiveTab,
  onZoomToData,
  getCurrentTableType,
  getCurrentTableData,
  getCurrentTableCounts,
  wellGroupingTableSort,
  setWellGroupingTableSort,
  handleWellGroupingTableSort,
  riskWellsTableSort,
  setRiskWellsTableSort,
  handleRiskWellsTableSort,
  multipleRecordsTableSort,
  setMultipleRecordsTableSort,
  handleMultipleRecordsTableSort,
  resetMultipleRecordsTableSort,
  violationsByRuleTableSort,
  setViolationsByRuleTableSort,
  handleViolationsByRuleTableSort,
  resetViolationsByRuleTableSort,
  riskWellsFilter,
  setRiskWellsFilter,
  resetRiskWellsFilter,
  inactiveYearsBinSize,
  setInactiveYearsBinSize,
  resetInactiveYearsBinSize,
  violationsFilter,
  setViolationsFilter,
  resetViolationsFilter,
  severancesFilter,
  setSeverancesFilter,
  resetSeverancesFilter,
  inspectionsFilter,
  setInspectionsFilter,
  resetInspectionsFilter,
  hiddenWellGroups,
  setHiddenWellGroups,
  resetHiddenWellGroups,
  hiddenViolationsLines,
  setHiddenViolationsLines,
  resetHiddenViolationsLines,
  hiddenReinspectionLines,
  setHiddenReinspectionLines,
  resetHiddenReinspectionLines,
  hiddenSeverancesLines,
  setHiddenSeverancesLines,
  resetHiddenSeverancesLines,
  hiddenSeveranceVolumeLines,
  setHiddenSeveranceVolumeLines,
  resetHiddenSeveranceVolumeLines,
  hiddenInspectionLines,
  setHiddenInspectionLines,
  resetHiddenInspectionLines,
  hiddenProductionLines,
  setHiddenProductionLines,
  resetHiddenProductionLines,
  hiddenFlaringLines,
  setHiddenFlaringLines,
  resetHiddenFlaringLines,
  hiddenFuelGasLines,
  setHiddenFuelGasLines,
  resetHiddenFuelGasLines,
  hiddenSpillLines,
  setHiddenSpillLines,
  resetHiddenSpillLines,
  hiddenSpillVolumeLines,
  setHiddenSpillVolumeLines,
  resetHiddenSpillVolumeLines,
  productionTableSort,
  handleProductionTableSort,
  resetProductionTableSort,
  inspectionsTableSort,
  handleInspectionsTableSort,
  resetInspectionsTableSort,
  violationsTableSort,
  handleViolationsTableSort,
  resetViolationsTableSort,
  severancesTableSort,
  handleSeverancesTableSort,
  resetSeverancesTableSort,
  spillsTableSort,
  handleSpillsTableSort,
  resetSpillsTableSort,
  getAllUniqueOperatorNames,
  setOperatorNamesDialogOpen,
  tierInfo,
  onGenerateReport,
  isGeneratingReport,
  formatTableResult,
  getUniqueOperatorNames,
  // Selection clearing callbacks
  onClearMapSelection,
  onRegisterClearCallback,
  // Lease navigation callbacks
  onRegisterNavigateToLeaseInSummary,
  navigateToLeaseInMapRef,
  // Search controls props
  loading,
  searchFormVisible,
  cancelSearch,
  handleSearchWrapper,
  setEditSearchDialogOpen,
  setRefineSearchDialogOpen,
  validateSearchParameters,
  handleResetWrapper,
  setSaveSearchDialogOpen,
  setAboutSearchDialogOpen,
  handleGenerateReport,
}) => {
  // State for forcing lease view mode
  const [forceLeaseView, setForceLeaseView] = useState(false);

  // Reset forceLeaseView after it's been applied
  useEffect(() => {
    if (forceLeaseView && activeTab === 1) {
      // Reset after a short delay to allow the view mode to update
      const timer = setTimeout(() => {
        setForceLeaseView(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [forceLeaseView, activeTab]);

  // Don't render if no search results
  if (!searchResults) return null;

  // Get available tabs based on actual search results data
  const getAvailableTabs = () => {
    const tabs = [];
    const tableTypes = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
    
    // Add Matches tab first if we have any data
    const hasAnyData = tableTypes.some(tableType => {
      const data = searchResults.data?.[tableType];
      return data && Array.isArray(data) && data.length > 0;
    });
    
    if (hasAnyData) {
      tabs.push({ label: 'Matches', value: 0 });
    }
    
    // Add Report Card tab if we have any data
    if (hasAnyData) {
      tabs.push({ label: 'Report Card', value: 1 });
    }
    
    let tabIndex = hasAnyData ? 2 : 0;
    tableTypes.forEach((tableType) => {
      const data = searchResults.data?.[tableType];
      if (data && Array.isArray(data) && data.length > 0) {
        const label = tableType.charAt(0).toUpperCase() + tableType.slice(1);
        tabs.push({ label, value: tabIndex });
        tabIndex++;
      }
    });
    
    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Don't render if no tabs available
  if (availableTabs.length === 0) {
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Search Controls Header */}
      <Box sx={{ px: 2, pb: 3, pt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={loading ? "outlined" : "contained"}
              onClick={loading ? cancelSearch : (searchFormVisible ? handleSearchWrapper : () => setEditSearchDialogOpen(true))}
              disabled={!loading && (!searchParams.state || !validateSearchParameters().isValid)}
              color="primary"
              size="small"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? 'Cancel' : (searchFormVisible ? 'Search' : 'Edit')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setRefineSearchDialogOpen(true)}
              color="primary"
              size="small"
            >
              Refine
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Filter Search Results" arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    // Switch to Report Card tab and set view mode to 'lease'
                    setActiveTab(1);
                    setForceLeaseView(true);
                  }}
                  disabled={!(searchResults && searchResults.data && Object.keys(searchResults.data).length > 0)}
                  sx={{
                    cursor: !(searchResults && searchResults.data && Object.keys(searchResults.data).length > 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <FilterIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Save Search" arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setSaveSearchDialogOpen(true)}
                  disabled={!searchParams.state || !validateSearchParameters().isValid || (tierInfo && !tierInfo.can_save_searches)}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_save_searches) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_save_searches) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Export Analytics as a PDF Report" arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleGenerateReport || onGenerateReport}
                  disabled={isGeneratingReport || (tierInfo && !tierInfo.can_generate_reports) || !(searchResults && searchResults.data && Object.keys(searchResults.data).length > 0)}
                  sx={{
                    cursor: (tierInfo && !tierInfo.can_generate_reports) || !(searchResults && searchResults.data && Object.keys(searchResults.data).length > 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="About Search" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setAboutSearchDialogOpen(true)}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Entire Search" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={handleResetWrapper}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Filter Indicator Banner - Show when filter is active */}
      {resultsFilter?.isFilterActive && (() => {
        // Calculate county and lease counts from selected lease keys
        const selectedLeaseKeys = resultsFilter.selectedLeaseKeys || new Set();
        const countiesSet = new Set();
        const leasesSet = new Set();
        
        // Map lease keys to counties by looking up in search results
        if (searchResults?.data?.wells && selectedLeaseKeys.size > 0) {
          searchResults.data.wells.forEach(well => {
            const district = well['District'] || well.district || '00';
            const leaseNumber = well['Lease Number'] || well.lease_number || null;
            const leaseName = well['Lease Name'] || well.lease_name || null;
            const county = well['County'] || well.county || null;
            
            // Generate lease key to match against selected keys
            const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
            
            if (selectedLeaseKeys.has(leaseKey)) {
              leasesSet.add(leaseKey);
              if (county) {
                countiesSet.add(county);
              }
            }
          });
        }
        
        const countyCount = countiesSet.size;
        const leaseCount = leasesSet.size;
        
        return (
          <Box sx={{ mb: 2, px: 2 }}>
            <Alert 
              severity="info"
              icon={<FilterIcon />}
              action={
                <IconButton
                  aria-label="clear filter"
                  color="inherit"
                  size="small"
                  onClick={() => resultsFilter?.clearFilter()}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '& .MuiAlert-icon': {
                  color: 'primary.contrastText'
                },
                '& .MuiAlert-action': {
                  color: 'primary.contrastText'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Filter Active:
                </Typography>
                <Typography variant="body2">
                  {countyCount} Countr{countyCount !== 1 ? 'ies' : 'y'}, {leaseCount} Lease{leaseCount !== 1 ? 's' : ''}
                </Typography>
                <Chip 
                  label="Clear Filter" 
                  size="small" 
                  onClick={() => resultsFilter?.clearFilter()}
                  sx={{ 
                    ml: 1,
                    backgroundColor: 'primary.contrastText',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.contrastText',
                      opacity: 0.9
                    }
                  }}
                />
              </Box>
            </Alert>
          </Box>
        );
      })()}

      {/* Analytics Tabs */}
      <Box sx={{ mb: 3, px: 2 }}>
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={true}
        >
          {availableTabs.map((tab) => (
            <Tab 
              key={tab.value}
              label={tab.label}
              value={tab.value}
              sx={{ 
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 700,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  transform: 'translateY(-1px)'
                }
              }}
            />
          ))}
        </Tabs>
      </Paper>
        
      </Box>

      {/* Tab Content */}
      <Box sx={{ px: 2 }}>
      {/* Matches Tab */}
      {activeTab === 0 && (
        <MatchesCard
          searchResults={searchResults}
          searchParams={searchParams}
          activityDateFilters={activityDateFilters}
          wellDateFilters={wellDateFilters}
          formatTableResult={formatTableResult}
          getUniqueOperatorNames={getUniqueOperatorNames}
          setOperatorNamesDialogOpen={setOperatorNamesDialogOpen}
          tierInfo={tierInfo}
          setActiveTab={setActiveTab}
        />
      )}
      
      {/* Report Card Tab - Always render (but hide when not active) to ensure callbacks are registered */}
      <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
        <RelationalTab
          searchResults={searchResults}
          searchParams={searchParams}
          tierInfo={tierInfo}
          resultsFilter={resultsFilter}
          forceViewMode={forceLeaseView ? 'lease' : null}
          onClearMapSelection={onClearMapSelection}
          onRegisterClearCallback={onRegisterClearCallback}
          onZoomToData={onZoomToData}
          onRegisterNavigateToLeaseInSummary={onRegisterNavigateToLeaseInSummary}
          navigateToLeaseInMapRef={navigateToLeaseInMapRef}
        />
      </Box>

      {getCurrentTableType() === 'wells' && (
        <WellsTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          wellGroupingTableSort={wellGroupingTableSort}
          setWellGroupingTableSort={setWellGroupingTableSort}
          handleWellGroupingTableSort={handleWellGroupingTableSort}
          riskWellsTableSort={riskWellsTableSort}
          setRiskWellsTableSort={setRiskWellsTableSort}
          handleRiskWellsTableSort={handleRiskWellsTableSort}
          multipleRecordsTableSort={multipleRecordsTableSort}
          setMultipleRecordsTableSort={setMultipleRecordsTableSort}
          handleMultipleRecordsTableSort={handleMultipleRecordsTableSort}
          resetMultipleRecordsTableSort={resetMultipleRecordsTableSort}
          riskWellsFilter={riskWellsFilter}
          setRiskWellsFilter={setRiskWellsFilter}
          resetRiskWellsFilter={resetRiskWellsFilter}
          inactiveYearsBinSize={inactiveYearsBinSize}
          setInactiveYearsBinSize={setInactiveYearsBinSize}
          resetInactiveYearsBinSize={resetInactiveYearsBinSize}
          hiddenWellGroups={hiddenWellGroups}
          setHiddenWellGroups={setHiddenWellGroups}
          resetHiddenWellGroups={resetHiddenWellGroups}
          tierInfo={tierInfo}
        />
      )}
      
      {getCurrentTableType() === 'production' && (
        <ProductionTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          productionTableSort={productionTableSort}
          handleProductionTableSort={handleProductionTableSort}
          resetProductionTableSort={resetProductionTableSort}
          hiddenProductionLines={hiddenProductionLines}
          setHiddenProductionLines={setHiddenProductionLines}
          resetHiddenProductionLines={resetHiddenProductionLines}
          hiddenFlaringLines={hiddenFlaringLines}
          setHiddenFlaringLines={setHiddenFlaringLines}
          resetHiddenFlaringLines={resetHiddenFlaringLines}
          hiddenFuelGasLines={hiddenFuelGasLines}
          setHiddenFuelGasLines={setHiddenFuelGasLines}
          resetHiddenFuelGasLines={resetHiddenFuelGasLines}
          tierInfo={tierInfo}
        />
      )}
      
      {getCurrentTableType() === 'inspections' && (
        <InspectionsTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          inspectionsTableSort={inspectionsTableSort}
          handleInspectionsTableSort={handleInspectionsTableSort}
          resetInspectionsTableSort={resetInspectionsTableSort}
          inspectionsFilter={inspectionsFilter}
          setInspectionsFilter={setInspectionsFilter}
          resetInspectionsFilter={resetInspectionsFilter}
          hiddenInspectionLines={hiddenInspectionLines}
          setHiddenInspectionLines={setHiddenInspectionLines}
          resetHiddenInspectionLines={resetHiddenInspectionLines}
          tierInfo={tierInfo}
        />
      )}
      
      {getCurrentTableType() === 'violations' && (
        <ViolationsTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          violationsTableSort={violationsTableSort}
          handleViolationsTableSort={handleViolationsTableSort}
          resetViolationsTableSort={resetViolationsTableSort}
          violationsByRuleTableSort={violationsByRuleTableSort}
          setViolationsByRuleTableSort={setViolationsByRuleTableSort}
          handleViolationsByRuleTableSort={handleViolationsByRuleTableSort}
          resetViolationsByRuleTableSort={resetViolationsByRuleTableSort}
          violationsFilter={violationsFilter}
          setViolationsFilter={setViolationsFilter}
          resetViolationsFilter={resetViolationsFilter}
          hiddenViolationsLines={hiddenViolationsLines}
          setHiddenViolationsLines={setHiddenViolationsLines}
          resetHiddenViolationsLines={resetHiddenViolationsLines}
          hiddenReinspectionLines={hiddenReinspectionLines}
          setHiddenReinspectionLines={setHiddenReinspectionLines}
          resetHiddenReinspectionLines={resetHiddenReinspectionLines}
          tierInfo={tierInfo}
        />
      )}
      
      {getCurrentTableType() === 'severances' && severancesTableSort && handleSeverancesTableSort && resetSeverancesTableSort && (
        <SeverancesTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          severancesTableSort={severancesTableSort}
          handleSeverancesTableSort={handleSeverancesTableSort}
          resetSeverancesTableSort={resetSeverancesTableSort}
          severancesFilter={severancesFilter}
          setSeverancesFilter={setSeverancesFilter}
          resetSeverancesFilter={resetSeverancesFilter}
          hiddenSeverancesLines={hiddenSeverancesLines}
          setHiddenSeverancesLines={setHiddenSeverancesLines}
          resetHiddenSeverancesLines={resetHiddenSeverancesLines}
          hiddenSeveranceVolumeLines={hiddenSeveranceVolumeLines}
          setHiddenSeveranceVolumeLines={setHiddenSeveranceVolumeLines}
          resetHiddenSeveranceVolumeLines={resetHiddenSeveranceVolumeLines}
          tierInfo={tierInfo}
        />
      )}

      {getCurrentTableType() === 'spills' && spillsTableSort && handleSpillsTableSort && resetSpillsTableSort && (
        <SpillsTab
          searchData={searchResults}
          getCurrentTableCounts={getCurrentTableCounts}
          getCurrentTableData={getCurrentTableData}
          getCurrentTableType={getCurrentTableType}
          spillsTableSort={spillsTableSort}
          handleSpillsTableSort={handleSpillsTableSort}
          resetSpillsTableSort={resetSpillsTableSort}
          hiddenSpillLines={hiddenSpillLines}
          setHiddenSpillLines={setHiddenSpillLines}
          resetHiddenSpillLines={resetHiddenSpillLines}
          hiddenSpillVolumeLines={hiddenSpillVolumeLines}
          setHiddenSpillVolumeLines={setHiddenSpillVolumeLines}
          resetHiddenSpillVolumeLines={resetHiddenSpillVolumeLines}
          tierInfo={tierInfo}
        />
      )}
      </Box>
    </Box>
  );
};

export default AnalyticsSection;
